<template>
  <v-dialog :model-value="true" fullscreen persistent transition="dialog-bottom-transition" :scrim="false" :retain-focus="false">
    <v-card class="d-flex flex-column h-100 bg-grey-lighten-4" style="border-radius: 0 !important;">

      <!-- Top Navbar/Toolbar with Integrated Stepper -->
      <v-toolbar color="white" flat border class="px-6">
        <div class="text-h6 font-weight-black text-blue-grey-darken-4 text-no-wrap mr-4">
          Configure your machine
        </div>

        <v-spacer />

        <!-- Integrated Stepper -->
        <div class="stepper-bar d-none d-sm-flex align-center justify-center py-1">
          <div class="stepper-dot" :class="{ active: configurator_step === 'configurator', completed: configurator_step !== 'configurator' }">1</div>
          <span class="stepper-label" :class="{ active: configurator_step === 'configurator', completed: configurator_step !== 'configurator' }">Configure</span>
          <div class="stepper-line"></div>
          <div class="stepper-dot" :class="{ active: configurator_step === 'summary', completed: ['contact', 'result'].includes(configurator_step) }">2</div>
          <span class="stepper-label" :class="{ active: configurator_step === 'summary', completed: ['contact', 'result'].includes(configurator_step) }">Installation</span>
          <div class="stepper-line"></div>
          <div class="stepper-dot" :class="{ active: ['contact', 'result'].includes(configurator_step) }">3</div>
          <span class="stepper-label" :class="{ active: ['contact', 'result'].includes(configurator_step) }">Confirm</span>
        </div>

        <v-spacer />

        <v-btn icon="mdi-close" variant="text" tabindex="-1" aria-label="Close configurator" @click="CloseConfigurator()" />
      </v-toolbar>

      <!-- Main Configurator Layout -->
      <div class="flex-grow-1 overflow-y-auto pa-2 pa-sm-4 pa-md-6">
        <v-window v-model="configurator_step" :touch="false">

          <!-- STEP 1: CONFIGURATOR OPTIONS -->
          <v-window-item value="configurator">
            <StepConfigurator
              :copier="copier"
              :accessories-fetching="accessories_fetching"
              :computed-monthly-price="ComputedMonthlyPrice"
              @toggle-dataset="ToggleDataset"
              @proceed="ProceedToSummary"
            />
          </v-window-item>

          <!-- STEP 2: PRICING SUMMARY & ADDRESS -->
          <v-window-item value="summary">
            <StepSummary
              :copier="copier"
              :cart-total-monthly-price="CartTotalMonthlyPrice"
              @remove-item="RemoveCartItem"
              @proceed="SubmitAddressStep"
            />
          </v-window-item>

          <!-- STEP 4: CONTACT DETAILS -->
          <v-window-item value="contact">
            <StepContact
              :cart-total-monthly-price="CartTotalMonthlyPrice"
              :preapproval-loading="preapproval_loading"
              :preapproval-error="preapproval_error"
              @remove-item="RemoveCartItem"
              @submit="SubmitContactStep"
            />
          </v-window-item>

          <!-- STEP 5: SUCCESS / RESULT -->
          <v-window-item value="result">
            <StepResult
              :copier="copier"
              :cart-total-monthly-price="CartTotalMonthlyPrice"
              :preapproval-status="preapproval_status"
              @close="CloseConfigurator"
            />
          </v-window-item>

        </v-window>
      </div>
    </v-card>
  </v-dialog>

  <!--Conflict Modal Teleported to Modal Shadow Root-->
  <Teleport :to="teleport_target" v-if="teleport_target && conflicts.modal">
    <div class="cm-backdrop" @click.self="CancelConflictAction">
      <div class="cm-modal">

        <!-- Header -->
        <div class="cm-header">
          <h2 class="cm-title">Compatibility Conflict</h2>
          <button class="cm-close-btn" @click="CancelConflictAction" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="cm-body">

          <!-- Alert Banner -->
          <div class="cm-alert-box">
            <div class="cm-alert-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div class="cm-alert-content">
              <p>{{ conflicts.heading || "You're adding:" }} <strong class="cm-highlight">{{ conflicts.title }}</strong></p>
              <p class="cm-alert-subtitle">{{ conflicts.subtext }}</p>
            </div>
          </div>

          <!-- Removals Group -->
          <div v-if="conflicts.todos.filter(t => !t.is_main && t.action === 'remove').length > 0" class="cm-list-section">
            <div class="cm-list-title cm-remove-title">To be removed</div>
            <div class="cm-item-list">
              <template v-for="todo in conflicts.todos.filter(t => !t.is_main && t.action === 'remove')" :key="todo.dataset.id">
                <div class="cm-item-card cm-remove-item">
                  <div class="cm-item-icon-wrapper">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </div>
                  <AccessoryName :accessory="Accessory(todo.dataset)" />
                </div>
              </template>
            </div>
          </div>

          <!-- Additions Group -->
          <div v-if="conflicts.todos.filter(t => !t.is_main && t.action === 'add').length > 0" class="cm-list-section">
            <div class="cm-list-title cm-add-title">To be added</div>
            <div class="cm-item-list">
              <template v-for="todo in conflicts.todos.filter(t => !t.is_main && t.action === 'add')" :key="todo.dataset.id">
                <div class="cm-item-card cm-add-item">
                  <div class="cm-item-icon-wrapper">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </div>
                  <AccessoryName :accessory="Accessory(todo.dataset)" />
                </div>
              </template>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="cm-footer">
          <button class="cm-btn cm-btn-cancel" @click="CancelConflictAction">Cancel</button>
          <button class="cm-btn cm-btn-continue" @click="CallConflictAction">Continue</button>
        </div>

      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import axios from 'axios'
import { storeToRefs } from 'pinia'
import Server from "@/plugins/Server"
import { useGlobalsStore } from '@/stores/globals'
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { CalculateProductMonthlyPrice, IsPrinterCategory, IsProductCustomQuote, IsProductQuotable } from '@/mixins/PricingMixins.ts'

// Imported Components
import AccessoryName from "@/components/configurator/AccessoryName.vue"

// Step Child Components
import StepResult from "@/components/configurator/StepResult.vue"
import StepSummary from "@/components/configurator/StepSummary.vue"
import StepContact from "@/components/configurator/StepContact.vue"
import StepConfigurator from "@/components/configurator/StepConfigurator.vue"

const store = useGlobalsStore()
const {
  cart_items,
  selected_copier,
  addtocart_trigger,
  form,
  configurator_step,
  run_preapprovals,
} = storeToRefs(store)

interface ConflictState {
  modal: boolean
  items: any[]
  title: string
  heading?: string
  subtext: string
  todos: any[]
}

const conflicts = ref<ConflictState>({
  modal: false,
  items: [],
  title: '',
  heading: '',
  subtext: '',
  todos: []
})

const copier = ref({
  instance: null as any,
  datasets: [] as any[],
  accessories: [] as any[]
})

const undo_list = ref<any[]>([])
const redo_list = ref<any[]>([])
const preapproval_error = ref('')
const preapproval_status = ref('')
const preapproval_loading = ref(false)
const activeCartIndex = ref<number | null>(null)
const accessories_fetching = ref<boolean>(false)
const teleport_target = ref<Element | null>(null)

/**
 * Resolves the Shadow DOM modal container host for Teleport targets
 */
function ResolveTeleportTarget() {
  const host = document.getElementById('one-800-products-modal-host')
  if (host && host.shadowRoot) {
    teleport_target.value = host.shadowRoot.getElementById('app-modal-root')
  } else if ((window as any).__showroom_modal_container) {
    teleport_target.value = (window as any).__showroom_modal_container
  } else {
    teleport_target.value = document.body
  }
}

ResolveTeleportTarget()


function SaveQuoteToDb() {
  // Clean cart items to keep only selected datasets and referenced accessories
  const cleanedCartItems = cart_items.value.map((item: any) => {
    const selectedDatasets = (item.datasets || []).filter((ds: any) => ds.selected)
    const selectedAccessories = (item.accessories || []).filter((acc: any) => {
      return selectedDatasets.some((ds: any) => ds.accessory_id === acc.id)
    })
    const rawProd = item.product || {}
    const productData = {
      id:                rawProd.id || null,
      name:              rawProd.name || '',
      model_number:      rawProd.model_number || '',
      slug:              rawProd.slug || '',
      advertising_price: rawProd.advertising_price || '0',
      price:             parseFloat(String(rawProd.advertising_price || rawProd.price || '0').replace(/[$,]/g, '')) || 0,
      printer_category:  rawProd.printer_category || 'Printer',
      product_image:     rawProd.product_image || '',
      copier_color:      rawProd.copier_color || '',
      prints_per_month:  parseInt(rawProd.prints_per_month) || 0,
      color_volume:      parseInt(rawProd.color_volume) || 0
    }
    return {
      quantity: item.qty ?? item.quantity ?? 1,
      product: productData,
      accessories: selectedAccessories.map((acc: any) => {
        const ds = selectedDatasets.find((d: any) => d.accessory_id === acc.id)
        return {
          qty:              acc.qty ?? acc.quantity ?? 1,
          price_margin:     acc.price_margin ?? 0,
          dataset_id:       ds?.id || acc.dataset_id || null,
          dataset_attached: true,
          accessory_id:     acc.id || acc.accessory_id || null,
          accessory_price:  parseFloat(String(acc.advertising_price || acc.price || acc.accessory_price || 0)) || 0,
          sku_no:           acc.sku_no || ''
        }
      })
    }
  })

  // Prepare payload
  const payload = {
    partner_id: (window as any).catalog_config?.partner_identifier || null,
    acquisition_type: form.value.acquisition_type,
    lease_term: form.value.acquisition_type === 'lease' ? form.value.lease_term : null,
    lead_source: window.location.origin || '',
    address: form.value.address,
    contact: form.value.contact,
    cart_items: cleanedCartItems
  }

  // Post to API and return promise
  return Server.post('/api/v1/quotes', payload)
}

function SubmitAddressStep() {
  configurator_step.value = 'contact'
}

async function SubmitContactStep() {
  preapproval_loading.value = true
  preapproval_error.value = ''

  try {
    // 1. Asynchronously save quote snapshot to Catalog Server
    const quoteResponse = await SaveQuoteToDb()
    const quote_id = quoteResponse?.data?.id || quoteResponse?.data?.quote_id || null
    const quote_pdf = quoteResponse?.data?.quote_pdf || quoteResponse?.data?.pdf_url || null

    const is_unpriced = cart_items.value.every((item: any) => IsProductCustomQuote(item.product))
    const is_all_quotable = cart_items.value.every((item: any) => IsProductQuotable(item.product))
    const is_active_contract = form.value.contract_status === 'active'

    const copier_models = cart_items.value.map((item: any) => item.product?.name).filter(Boolean).join(', ')
    let total_print_volume = cart_items.value.reduce((total: number, item: any) => {
      const mono = Number(item.product?.prints_per_month) || 0
      const color = Number(item.product?.color_volume) || 0
      return total + mono + color
    }, 0)
    if (total_print_volume === 0 && copier.value?.instance) {
      const mono = Number(copier.value.instance.prints_per_month) || 0
      const color = Number(copier.value.instance.color_volume) || 0
      total_print_volume = mono + color
    }

    // Build structured cart_items JSON snapshot matching OS Deals database specification
    const formatted_cart_items = cart_items.value.map((item: any) => {
      const selectedDatasets = (item.datasets || []).filter((ds: any) => ds.selected)
      const selectedAccessories = (item.accessories || []).filter((acc: any) => {
        return selectedDatasets.some((ds: any) => ds.accessory_id === acc.id)
      })

      return {
        quantity: item.qty ?? item.quantity ?? 1,
        prints_included: {
          mono: item.product?.prints_per_month || 0,
          color: item.product?.color_volume || 0
        },
        product: {
          id:                item.product?.id || item.product?.product_id || null,
          manufacturer_id:   item.product?.manufacturer_id || null,
          name:              item.product?.name || '',
          model_number:      item.product?.model_number || '',
          slug:              item.product?.slug || '',
          price:             parseFloat(String(item.product?.advertising_price || '').replace(/[$,]/g, '')) || 0,
          advertising_price: item.product?.advertising_price || '',
          printer_category:  item.product?.printer_category || '',
          product_image:     item.product?.product_image || '',
          duplex_printing:   item.product?.duplex_printing || '',
          paper_capacity:    item.product?.paper_capacity || '',
          mono_cpp:          item.product?.mono_cpp || '',
          color_cpp:         item.product?.color_cpp || '',
          connectivity:      item.product?.connectivity || []
        },
        accessories: selectedAccessories.map((acc: any) => {
          const ds = selectedDatasets.find((d: any) => d.accessory_id === acc.id)
          return {
            name:           acc.name || '',
            quantity:       acc.qty ?? acc.quantity ?? 1,
            price:          parseFloat(String(acc.advertising_price || acc.price || 0)) || 0,
            featured_image: acc.featured_image || '',
            accessory_id:   acc.id || acc.accessory_id || null,
            dataset_id:     ds?.id || null
          }
        }),
        datasets: selectedDatasets.map((ds: any) => ({
          id:           ds.id,
          product_id:   ds.product_id,
          accessory_id: ds.accessory_id,
          selected:     true
        }))
      }
    })

    // =========================================================================
    // ROUTE 1: If run_preapprovals is TRUE and all items are quotable (Flagship Site -> OS Deals Ingest)
    // =========================================================================
    if (run_preapprovals.value && is_all_quotable) {
      if (is_active_contract) {
        // Path 1: Service / Upgrade Ingest
        const first_cart_item = cart_items.value[0] || {}
        const machine_brand = first_cart_item.product?.manufacturer_name || copier.value?.instance?.manufacturer_name || 'Kyocera'
        const machine_model = first_cart_item.product?.name || copier.value?.instance?.name || 'Office Copier'

        await axios.post(`${import.meta.env.VITE_PREAPPROVAL_URL}/api/service/ingest`, {
          partner_id:      (window as any).catalog_config?.partner_identifier || null,
          quote_id:        quote_id,
          business_name:   form.value.contact.business_name,
          first_name:      form.value.contact.first_name,
          last_name:       form.value.contact.last_name,
          email:           form.value.contact.email,
          phone:           form.value.contact.phone,
          street_address:  form.value.address.street,
          city:            form.value.address.city,
          state:           form.value.address.state,
          zip_code:        form.value.address.zip,
          monthly_payment: form.value.acquisition_type === 'lease' ? CartTotalMonthlyPrice.value : null,
          lease_term:      form.value.acquisition_type === 'lease' ? (form.value.lease_term || 60) : null,
          equipment_type:  copier_models || 'Office Copiers & Printers',
          cart_items:      formatted_cart_items,
          opportunity_details: {
            type:                'service',
            machine_brand:       machine_brand,
            machine_model:       machine_model,
            monthly_volume:      total_print_volume,
            contract_status:     'under_contract',
            service_reason:      'upgrade_opportunity',
            service_tier:        'upgrade_opportunity',
            parts_status:        'Available ✓',
            issue_description:   'Active contract client requested equipment quote / service options via OS Showroom'
          }
        })
      } else {
        // Path 2: Buyer Lead Ingest (is_hot: 1 for lease on priced printer, is_hot: 0 for purchase / unpriced)
        const is_hot = (form.value.acquisition_type === 'lease' && !is_unpriced) ? 1 : 0
        const color_or_bw = cart_items.value.some((item: any) => item.product?.copier_color !== 'B&W') ? 'Color' : 'B&W'

        await axios.post(`${import.meta.env.VITE_PREAPPROVAL_URL}/api/leads/ingest`, {
          partner_id:      (window as any).catalog_config?.partner_identifier || null,
          quote_id:        quote_id,
          business_name:   form.value.contact.business_name,
          first_name:      form.value.contact.first_name,
          last_name:       form.value.contact.last_name,
          email:           form.value.contact.email,
          phone:           form.value.contact.phone,
          street_address:  form.value.address.street,
          city:            form.value.address.city,
          state:           form.value.address.state,
          zip_code:        form.value.address.zip,
          is_hot:          is_hot,
          monthly_payment: form.value.acquisition_type === 'lease' ? CartTotalMonthlyPrice.value : null,
          lease_term:      form.value.acquisition_type === 'lease' ? (form.value.lease_term || 60) : null,
          equipment_type:  copier_models || 'Office Copiers & Printers',
          cart_items:      formatted_cart_items,
          opportunity_details: {
            type: 'lead',
            monthly_volume: total_print_volume,
            color_or_bw: color_or_bw,
            device_count: cart_items.value.length || 1,
            timeframe: 'immediate',
            form_factor: 'standalone_copier',
            notes: is_hot === 1
              ? `Hot Buyer Lead: Customer configured ${form.value.lease_term || 60}-mo lease with no active contract.`
              : `Buyer Inquiry: Outright purchase / custom quote request (${copier_models}).`,
            current_lease: {
              has_existing_lease: false
            }
          }
        })
      }
    }
    // =========================================================================
    // ROUTE 2: If run_preapprovals is FALSE (10+ Client Websites -> GHL Webhook)
    // =========================================================================
    else {
      let lead_message = 'OS Showroom: Customer quote request'
      let lead_source = 'OS Showroom - Quote Request'
      if (is_active_contract) {
        lead_message = 'OS Showroom: Active contract customer inquiry'
        lead_source = 'OS Showroom - Active Contract Lead'
      } else if (form.value.acquisition_type === 'purchase') {
        lead_message = 'OS Showroom: Customer purchase inquiry'
        lead_source = 'OS Showroom - Purchase Request'
      } else if (is_unpriced) {
        lead_message = 'OS Showroom: Custom pricing quote request (Unpriced equipment)'
        lead_source = 'OS Showroom - Custom Quote Request'
      } else {
        lead_message = `OS Showroom: Lease quote request (${form.value.lease_term || 60} mo lease)`
        lead_source = 'OS Showroom - Lease Quote Request'
      }

      await axios.post('/wp-json/1-800-products/v1/service-contract-lead', {
        first_name: form.value.contact.first_name,
        last_name: form.value.contact.last_name,
        email: form.value.contact.email,
        phone: form.value.contact.phone,
        company: form.value.contact.business_name,
        address: {
          street: form.value.address.street,
          suite: form.value.address.suite || '',
          city: form.value.address.city,
          state: form.value.address.state,
          zip: form.value.address.zip
        },
        copier_model: copier_models,
        lease_term: form.value.acquisition_type === 'lease' ? (form.value.lease_term || 60) : 0,
        print_volume: total_print_volume,
        quote_id: quote_id,
        quote_pdf: quote_pdf,
        contract_status: form.value.contract_status,
        message: lead_message,
        source: lead_source
      }).catch(err => console.error('Failed sending lead to ConnectM8 / GHL:', err))
    }
  } catch (err: any) {
    console.error('Failed submitting lead:', err)
  } finally {
    preapproval_loading.value = false
    configurator_step.value = 'result'
  }
}

onUnmounted(() => {
  document.documentElement.style.overflow = ''
  document.body.style.overflow = ''
})

function CloseConfigurator() {
  document.documentElement.style.overflow = ''
  document.body.style.overflow = ''
  copier.value = {
    instance: null,
    datasets: [],
    accessories: []
  }
  activeCartIndex.value = null
  store.SetState('selected_copier', null)
  store.SetState('configurator', false)
  configurator_step.value = 'configurator'
}

function Accessory(dataset: any) {
  if (!dataset) return { name: '', part_number: '', advertising_price: 0, description: '' }
  return copier.value.accessories.find((acc: any) => acc.id == dataset.accessory_id || acc.id == dataset.id) || { name: '', part_number: '', advertising_price: 0, description: '' }
}

function Dataset(id: any) {
  return copier.value.datasets.find((ds: any) => ds.id == id || ds.accessory_id == id)
}

function ToggleDataset(ds: any) {
  if (ds.selected) {
    UnselectDataset(ds)
  } else {
    SelectDataset(ds)
  }
}

/**
 * Validates accessory rules and triggers the conflict modal if dependencies/incompatibilities exist
 */
function SelectDataset(dataset: any) {
  CancelConflictAction()
  const rules = Array.isArray(dataset.rules) ? dataset.rules : (typeof dataset.rules === 'string' ? JSON.parse(dataset.rules || '[]') : [])

  let has_error = false
  const incompatibles: any[] = []
  const requires_all_missing: any[] = []

  // 1. Check direct rules defined on the dataset being added
  rules.forEach((rule: any) => {
    if (has_error) return
    const rule_items = Array.isArray(rule.items) ? rule.items : Object.values(rule.items || {})

    if (rule.type == 'incompatible_with') {
      rule_items.forEach((ds_id: any) => {
        const found = Dataset(ds_id)
        if (found && found.selected) {
          if (!incompatibles.some((inc: any) => inc.id == found.id)) {
            incompatibles.push(found)
          }
        }
      })
    } else if (rule.type == 'require_one') {
      let passed = false
      let items_txt = "\n"
      rule_items.forEach((ds: any) => {
        const found = Dataset(ds)
        if (found) {
          items_txt += '- ' + Accessory(found).name + '\n'
          if (found.selected) passed = true
        }
      })
      if (!passed) {
        dataset.selected = false
        CancelConflictAction()
        store.SetError(`This accessory requires one of the following: ${items_txt}`)
        has_error = true
      }
    } else if (rule.type == 'require_all') {
      rule_items.forEach((ds: any) => {
        const found = Dataset(ds)
        if (!found || !found.selected) {
          if (found && !requires_all_missing.some((req: any) => req.id == found.id)) {
            requires_all_missing.push(found)
          }
        }
      })
    }
  })

  // 2. Reverse check: check if any currently selected accessory is incompatible with this item
  if (!has_error && copier.value?.datasets) {
    copier.value.datasets.forEach((ds: any) => {
      if (!ds.selected || ds.id == dataset.id) return
      const ds_rules = Array.isArray(ds.rules) ? ds.rules : (typeof ds.rules === 'string' ? JSON.parse(ds.rules || '[]') : [])
      ds_rules.forEach((r: any) => {
        if (r.type === 'incompatible_with') {
          const r_items = Array.isArray(r.items) ? r.items : Object.values(r.items || {})
          if (r_items.some((item: any) => item == dataset.id || item == dataset.accessory_id)) {
            if (!incompatibles.some((inc: any) => inc.id == ds.id)) {
              incompatibles.push(ds)
            }
          }
        }
      })
    })
  }

  if (has_error) return

  if (incompatibles.length > 0 || requires_all_missing.length > 0) {
    conflicts.value.title = Accessory(dataset).name
    conflicts.value.heading = "You're adding:"
    conflicts.value.modal = true
    conflicts.value.items = []
    
    const subtexts: string[] = []
    
    if (incompatibles.length > 0) {
      subtexts.push('remove incompatible items')
      incompatibles.forEach(conflict => {
        conflicts.value.todos.push({
          action: 'remove',
          dataset: conflict,
          is_main: false
        })
      })
    }
    
    if (requires_all_missing.length > 0) {
      subtexts.push('add required items')
      requires_all_missing.forEach(found => {
        conflicts.value.todos.push({
          action: 'add',
          dataset: found,
          is_main: false
        })
      })
    }
    
    conflicts.value.subtext = "To proceed, we will automatically " + subtexts.join(' and ') + ":"
    
    conflicts.value.todos.push({ action: 'add', dataset: dataset, is_main: true })
    return
  }

  // All rules passed successfully
  dataset.selected = true
  if (!undo_list.value.includes(dataset.id)) undo_list.value.push(dataset.id)
  store.SetSuccess('Accessory added')
}

function CallConflictAction() {
  conflicts.value.todos.forEach(todo => {
    if (todo.action == 'remove') {
      todo.dataset.selected = false
    }
    if (todo.action == 'add') {
      todo.dataset.selected = true
      store.SetSuccess('Accessory added')
    }
  })
  CancelConflictAction()
}

function CancelConflictAction() {
  conflicts.value = {
    modal: false,
    items: [],
    title: '',
    heading: '',
    subtext: '',
    todos: []
  }
}

function UnselectDataset(dataset: any) {
  const conflictsList = DatasetsThatRequiresThis(dataset)
  if (conflictsList.length < 1) {
    dataset.selected = false
    if (undo_list.value.includes(dataset.id)) {
      const index = undo_list.value.findIndex(id => id == dataset.id)
      undo_list.value.splice(index, 1)
    }
    if (!redo_list.value.includes(dataset.id)) {
      redo_list.value.push(dataset.id)
    }
  } else {
    conflicts.value.items = conflictsList
    conflicts.value.title = Accessory(dataset).name
    conflicts.value.heading = "You're removing:"
    conflicts.value.subtext = 'The following accessories will be removed:'
    conflicts.value.modal = true
    conflicts.value.todos.push({ action: 'remove', dataset: dataset, is_main: true })
    conflictsList.forEach(conflict => {
      conflicts.value.todos.push({
        action: 'remove',
        dataset: conflict,
        is_main: false
      })
    })
  }
}

function DatasetsThatRequiresThis(dataset: any) {
  const list: any[] = []
  copier.value.datasets.forEach(ds => {
    if (ds.id == dataset.id) return
    const rules = Array.isArray(ds.rules) ? ds.rules : (typeof ds.rules === 'string' ? JSON.parse(ds.rules || '[]') : [])
    rules.forEach((rule: any) => {
      if (rule.type == 'require_one' || rule.type == 'require_all') {
        const rule_items = Array.isArray(rule.items) ? rule.items : Object.values(rule.items || {})
        rule_items.forEach((item: any) => {
          if ((item == dataset.id || item == dataset.accessory_id) && ds.selected) {
            if (!list.some((existing: any) => existing.id == ds.id)) {
              list.push(ds)
            }
          }
        })
      }
    })
  })
  return list
}

function AddToCart() {
  const current_items = [...cart_items.value]
  current_items.push({
    product: JSON.parse(JSON.stringify(copier.value.instance)),
    datasets: JSON.parse(JSON.stringify(copier.value.datasets)),
    accessories: JSON.parse(JSON.stringify(copier.value.accessories)),
    quantity: 1,
  })
  store.SetCartItems(current_items)
  store.SetSuccess("1 item(s) added to quote")
}

function ProceedToSummary() {
  const item = {
    product: JSON.parse(JSON.stringify(copier.value.instance)),
    datasets: JSON.parse(JSON.stringify(copier.value.datasets)),
    accessories: JSON.parse(JSON.stringify(copier.value.accessories)),
    quantity: 1,
  }
  const current_items = [...cart_items.value]
  if (activeCartIndex.value !== null && activeCartIndex.value >= 0 && activeCartIndex.value < current_items.length) {
    current_items[activeCartIndex.value] = item
  } else {
    current_items.push(item)
    activeCartIndex.value = current_items.length - 1
  }
  store.SetCartItems(current_items)
  configurator_step.value = 'summary'
}

function RemoveCartItem(idx: number) {
  if (cart_items.value.length <= 1) return

  const current_items = [...cart_items.value]

  if (activeCartIndex.value === idx) {
    // If removing the active item, point active to the new last item
    current_items.splice(idx, 1)
    store.SetCartItems(current_items)

    const newIndex = current_items.length - 1
    activeCartIndex.value = newIndex
    copier.value.instance = current_items[newIndex].product
    copier.value.datasets = current_items[newIndex].datasets
    copier.value.accessories = current_items[newIndex].accessories
  } else {
    // Just remove it, and adjust the active index if it was after the removed index
    current_items.splice(idx, 1)
    store.SetCartItems(current_items)
    if (activeCartIndex.value !== null && activeCartIndex.value > idx) {
      activeCartIndex.value--
    }
  }
}

// Dynamic Monthly Price Calculator for single active machine
const ComputedMonthlyPrice = computed(() => {
  if (!copier.value.instance) return 0
  return CalculateProductMonthlyPrice(copier.value.instance, copier.value.datasets, copier.value.accessories)
})

// Total price of all items currently in cart
const CartTotalMonthlyPrice = computed(() => {
  return cart_items.value.reduce((total: number, item: any) => {
    return total + CalculateProductMonthlyPrice(item.product, item.datasets, item.accessories || item.product.accessories)
  }, 0)
})

onMounted(() => {
  ResolveTeleportTarget()
  document.documentElement.style.overflow = 'hidden'
  document.body.style.overflow = 'hidden'

  if (selected_copier.value) {
    copier.value.instance = selected_copier.value

    // For non-printer equipment, default acquisition to 'purchase' (quote request)
    if (!IsPrinterCategory(copier.value.instance)) {
      form.value.acquisition_type = 'purchase'
    }

    // Initialize properties directly on the product instance if not already defined
    if (copier.value.instance.prints_per_month === undefined) {
      copier.value.instance.prints_per_month = 500
    }
    if (copier.value.instance.copier_color === 'B&W') {
      copier.value.instance.color_volume = 0
    } else if (copier.value.instance.color_volume === undefined) {
      copier.value.instance.color_volume = 100
    }

    accessories_fetching.value = true
    Server.get(`/api/v1/showroom-dependencies/${copier.value.instance.id}`).then(res => {
      copier.value.datasets = res.data.datasets || []
      copier.value.accessories = res.data.accessories || []
    }).finally(() => {
      accessories_fetching.value = false
    });
  }
})

onUnmounted(() => {
  store.SetSelectedCopier(null)
  copier.value = {
    instance: null,
    datasets: [],
    accessories: []
  }
})

watch(addtocart_trigger, () => {
  AddToCart()
})
</script>

<style scoped lang="scss">
.max-width-container {
  max-width: 1600px !important;
  margin: 0 auto !important;
  width: 100% !important;
}
.border-card {
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
}
.badge-added {
  background-color: #e6f4ea !important;
  color: #137333 !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.2px;
}
.diagram-container {
  background-color: #f8fafc;
  border-radius: 20px;
  border: 1px dashed rgba(148, 163, 184, 0.2);
  min-height: 280px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.addon-option-card {
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
  transition: all 0.2s ease;
  background: white;
  min-height: 80px;

  &:hover {
    border-color: #1d4ed8 !important;
  }

  &.card-active {
    border-color: #16a34a !important;
    background-color: #f0fdf4;
    border-width: 2px !important;
  }
}
.disabled-card {
  background-color: #f8fafc !important;
  opacity: 0.85;
  cursor: not-allowed;
}
.shadow-quote {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.04);
}
.safe-badge-container {
  background-color: #f0fdf4;
}
.border-bottom-thin {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.acq-toggle, .term-toggle {
  border: 1px solid rgba(148, 163, 184, 0.2);
  width: 100%;
}
.acq-toggle button, .term-toggle button {
  flex-grow: 1;
}
.price-val {
  letter-spacing: -1px;
  font-size: 50px;
}
.cta-btn {
  background-color: #e06927 !important;
  color: white !important;
  font-size: 1.1rem !important;
  height: 52px !important;
}
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.clip-text-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Stepper bar */
.stepper-bar {
  gap: 0;
}
.stepper-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #b0bec5;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 800;
  flex-shrink: 0;
}
.stepper-dot.active {
  background: #e06927;
}
.stepper-dot.completed {
  background: #263238;
}
.stepper-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #263238;
  margin-left: 6px;
  margin-right: 4px;
  white-space: nowrap;
}
.stepper-label.active {
  color: #e06927;
}
.stepper-label.completed {
  color: #263238;
}
.stepper-line {
  width: 40px;
  height: 2px;
  background: #b0bec5;
  flex-shrink: 0;
  margin: 0 4px;
}

@media (max-width: 500px) {
  .stepper-label {
    display: none;
  }
  .stepper-line {
    width: 35px;
    margin: 0 8px;
  }
}
/* ===== Conflict Modal ===== */
.cm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}
.cm-modal {
  background: #ffffff;
  width: 100%;
  max-width: 560px;
  border-radius: 20px;
  box-shadow: 0 20px 25px -5px rgba(0,0,0,0.12), 0 10px 10px -5px rgba(0,0,0,0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: cm-fade-in 0.28s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes cm-fade-in {
  from { opacity: 0; transform: translateY(18px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.cm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 28px 14px;
}
.cm-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}
.cm-close-btn {
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 7px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s, color 0.18s;
  &:hover { background: #f3f4f6; color: #111827; }
}
.cm-body {
  padding: 0 28px 20px;
  overflow-y: auto;
  max-height: 66vh;
}
.cm-alert-box {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 22px;
}
.cm-alert-icon {
  color: #f59e0b;
  flex-shrink: 0;
  margin-top: 1px;
}
.cm-alert-content p {
  font-size: 0.93rem;
  line-height: 1.5;
  color: #111827;
  margin: 0;
}
.cm-alert-subtitle {
  margin-top: 3px !important;
  color: #6b7280 !important;
  font-size: 0.87rem !important;
}
.cm-highlight {
  color: #b45309;
  font-weight: 600;
}
.cm-list-section {
  margin-bottom: 20px;
  &:last-child { margin-bottom: 0; }
}
.cm-list-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}
.cm-remove-title { color: #ef4444; }
.cm-add-title    { color: #10b981; }
.cm-item-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cm-item-card {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  gap: 14px;
  transition: background 0.18s, box-shadow 0.18s, transform 0.18s;
  &:hover {
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    transform: translateY(-1px);
  }
}
.cm-item-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cm-remove-item .cm-item-icon-wrapper {
  background: #fef2f2;
  color: #ef4444;
  border: 1px solid #fecaca;
}
.cm-add-item .cm-item-icon-wrapper {
  background: #ecfdf5;
  color: #10b981;
  border: 1px solid #a7f3d0;
}
.cm-footer {
  padding: 18px 28px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #fafafa;
}
.cm-btn {
  padding: 9px 20px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.92rem;
  cursor: pointer;
  transition: background 0.18s, box-shadow 0.18s;
  border: none;
  font-family: inherit;
}
.cm-btn-cancel {
  background: transparent;
  color: #374151;
  &:hover { background: #f3f4f6; }
}
.cm-btn-continue {
  background: #2563eb;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
  &:hover { background: #1d4ed8; box-shadow: 0 3px 8px rgba(0,0,0,0.14); }
}

/* WordPress Admin Bar Compatibility */
.has-admin-bar .v-dialog--fullscreen .v-overlay__content,
:global(.has-admin-bar .v-dialog--fullscreen .v-overlay__content),
.has-admin-bar .cm-backdrop {
  top: 32px !important;
  height: calc(100vh - 32px) !important;
  max-height: calc(100vh - 32px) !important;
}

@media screen and (max-width: 782px) {
  .has-admin-bar .v-dialog--fullscreen .v-overlay__content,
  :global(.has-admin-bar .v-dialog--fullscreen .v-overlay__content),
  .has-admin-bar .cm-backdrop {
    top: 46px !important;
    height: calc(100vh - 46px) !important;
    max-height: calc(100vh - 46px) !important;
  }
}
</style>
